import { styled } from '@mui/system'
import { FloatingPanel } from 'components/floating/index'
import Histogram from 'components/histogram/index'
import { V, VItem } from 'components/layout/index'
import {
  Button,
  FormControlLabel,
  FormGroup,
  IconButton,
  Slider,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import EditOffIcon from '@mui/icons-material/EditOff'
import { colors } from 'components/foundation'
import { vectorLayers, rasterLayers, type LayerType } from 'common/layer-data'
import { useState, useEffect, type ReactNode, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import type { LegendURL } from 'state/map'
import { type AppState } from 'state/index'
import { showModal } from 'actions/app'
import { useDispatch } from 'react-redux'
import * as c from 'common/c'

import EditLegend from './edit-legend'
import { setLayerOpacity } from 'actions/map'
import { setLegendType } from 'actions/field'
import { get, pick, isNil } from 'es-toolkit/compat'
import useDebounce from 'hooks/util/debounce'
import { trackEvent } from 'api/usage-tracking'
import FeatureFlagsContext from 'context/feature-flags-context'
import React from 'react'
import { type CropConstant } from 'state/crop'
import { isSubscriptionValid } from 'common/misc'
import EqualRangeLegend from './equal-range-legend'
import GeoserverImage from 'components/geoserver-image'
import {
  type CustomLegend,
  type LayerInfoModel,
  type LegendTypeItem,
} from 'state/field'

const LegendControlSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    '&.Mui-checked + .MuiSwitch-track': {
      backgroundColor: theme.palette.primary.main,
      opacity: 1,
    },
    '& + .MuiSwitch-track': {
      background: colors.greyLight,
      opacity: 1,
    },
  },
  '& .MuiSwitch-thumb': {
    width: 15,
    height: 15,
    margin: 2,
    background: colors.white,
  },
  '& .MuiSwitch-track': {
    borderRadius: 10,
  },
}))

const getLegendParameterForLayer = (
  layerName?: string,
  swathParameter?: string,
) => {
  if (layerName === 'swath') {
    return swathParameter ?? 'yield'
  }

  if (layerName === 'dem') {
    return 'dem'
  }

  return 'yield'
}

export default function LayerLegend(props: {
  url: LegendURL[]
  harvestId: number
  lpVersion: string
  lpProcessDateTime: string
  opacity: number
  crop?: CropConstant
  id: string
  swathParameter?: string
  layerInfo?: LayerInfoModel[]
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const permissions = useSelector(
    (state: AppState) => state.account.permissions,
  )

  const customClaims = useSelector(
    (state: AppState) => state.account.customClaims,
  )

  const { harvestId, url, lpVersion, lpProcessDateTime, crop, id } = props
  const cropBuPerTonne = crop?.buPerTonne ?? 1.0

  const isAdmin =
    permissions.includes('farmtrx:admin') || customClaims?.[c.FT_ADMIN_CLAIM]
  const [filteredUrls, setFilteredUrls] = useState<LegendURL[]>([])
  const [legendItems, setLegendItems] = useState<ReactNode>([])
  const [histogramLayerName, setHistogramLayerName] = useState<any>('')
  const features = React.useContext(FeatureFlagsContext)

  const opacity = useSelector((state) => get(state, 'map.layerOpacity', 1.0))
  const invokeDebouncedUsageTracking = useDebounce(() => {
    trackEvent('layer_opacity_changed')
  }, 25000)
  const layerInfo = props?.layerInfo || []

  const subscriptions = useSelector(
    (state: AppState) => state.account.subscriptions,
  )
  const view = useSelector((state: AppState) => state.app.view)

  const selectedSwathParameter = props?.swathParameter || 'yield'

  const [validSubscription, setValidSubscription] = useState(false)

  const legendTypes: LegendTypeItem[] = useSelector(
    (state: AppState) => state.field.legendTypes,
  )
  const [customLegendExists, setCustomLegendExists] = useState(false)

  const [selectedLayer, setSelectedLayer] = useState<LayerType | null>(null)

  useEffect(() => {
    const legendTypeValue = legendTypes.find(
      (l) =>
        l.harvestId === harvestId &&
        (selectedLayer?.name === 'swath'
          ? l.swathParameter === selectedSwathParameter
          : l.layerName === selectedLayer?.name),
    ) as LegendTypeItem | undefined

    if (customLegendExists && legendTypeValue === undefined && harvestId) {
      dispatch(
        setLegendType({
          layerName: selectedLayer?.name,
          useCustomLegend: customLegendExists,
          harvestId: harvestId,
          swathParameter:
            selectedLayer?.name === 'swath'
              ? selectedSwathParameter
              : undefined,
        }),
      )
    }
  }, [customLegendExists, selectedLayer, harvestId, selectedSwathParameter])

  useEffect(() => {
    const _filteredUrls: LegendURL[] = url.filter(
      (el) => el.harvestId === harvestId,
    )
    setFilteredUrls(_filteredUrls)
  }, [url, harvestId])

  useEffect(() => {
    const startTime = new Date().toISOString().split('T')[0]
    setValidSubscription(
      isSubscriptionValid(subscriptions, startTime, startTime),
    )
  }, [subscriptions])
  const getLegend = useCallback(
    (layer, layerUrl) => {
      if (layerInfo && layerInfo.length > 0) {
        const layerDetails = layerInfo.find(
          (l) => l.harvestId === harvestId && l.layerName === layer.name,
        )
        const legendTypeValue = legendTypes.find(
          (l) =>
            l.harvestId === harvestId &&
            (selectedLayer?.name === 'swath'
              ? l.swathParameter === selectedSwathParameter &&
                l.layerName === 'swath'
              : l.layerName === layer.name),
        )
        if (
          c.customLegendLayerTypes.includes(layer.name) &&
          layerDetails &&
          layerDetails.customLegends &&
          layerDetails.customLegends.length > 0 &&
          layerDetails.customLegends[0] !== null
        ) {
          let customLegendData
          if (layer.name === 'swath') {
            customLegendData =
              Array.isArray(layerDetails?.customLegends) &&
              layerDetails.customLegends.find(
                (legend) =>
                  legend.parameter === (selectedSwathParameter ?? 'yield'),
              )
          } else if (layer.name === 'dem') {
            customLegendData =
              Array.isArray(layerDetails?.customLegends) &&
              layerDetails.customLegends.find(
                (legend) => legend.parameter === 'dem',
              )
          } else {
            customLegendData = layerDetails?.customLegends[0]
          }
          setCustomLegendExists(customLegendData ? true : false)

          if (
            customLegendData &&
            (legendTypeValue === undefined || legendTypeValue.useCustomLegend)
          ) {
            if (layer.name === 'dem') {
              return (
                <VItem key={`${layer.name}_itm`} $space={false} style={{}}>
                  <GeoserverImage
                    key={`${layer.name}_img`}
                    src={layerUrl.url}
                    alt="legend"
                  />
                </VItem>
              )
            }

            return (
              <EqualRangeLegend
                key={`${layer.name}_minmax`}
                layer={pick(layer, ['name', 'translation'])}
                colors={customLegendData.legendData.ranges.c}
                quantities={customLegendData.legendData.ranges.q}
                numRanges={customLegendData.legendData['num_ranges']}
                cropBuPerTonne={cropBuPerTonne}
                selectedSwathParameter={selectedSwathParameter}
                showUnits={true}
              ></EqualRangeLegend>
            )
          } else {
            return (
              <VItem key={`${layer.name}_itm`} $space={false} style={{}}>
                <GeoserverImage
                  key={`${layer.name}_img`}
                  src={layerUrl.url}
                  alt="legend"
                />
              </VItem>
            )
          }
        } else {
          setCustomLegendExists(false)
          return (
            <VItem key={`${layer.name}_itm`} $space={false} style={{}}>
              <GeoserverImage
                key={`${layer.name}_img`}
                src={layerUrl.url}
                alt="legend"
              />
            </VItem>
          )
        }
      }
    },
    [layerInfo, harvestId, selectedSwathParameter, legendTypes, cropBuPerTonne],
  )

  useEffect(() => {
    let _legendItems: ReactNode = []
    const vectorItems = vectorLayers.map((layer) => {
      const layerUrl = filteredUrls.find(
        (obj) => obj.name === layer.name && obj.harvestId === harvestId,
      )
      return layerUrl ? ( //@ts-expect-error
        <V key={`${layer.name}_container`}>
          {
            <VItem key={layer.translation} $space={false} style={{}}>
              <LegendHeader>{t(layer.translation)}</LegendHeader>
            </VItem>
          }
          {
            <VItem key={`${layer.name}_itm`} $space={false} style={{}}>
              <GeoserverImage
                key={`${layer.name}_img`}
                src={layerUrl.url}
                alt="legend"
              />
            </VItem>
          }
        </V>
      ) : null
    })
    setHistogramLayerName('')
    const rasterItems = rasterLayers.map((layer) => {
      const layerUrl = filteredUrls.find(
        (obj) => obj.name === layer.name && obj.harvestId === harvestId,
      )
      if (layerUrl) {
        setSelectedLayer(layer)
        if (layer.name === 'corrected_yield' || layer.name === 'raw_raster') {
          setHistogramLayerName(layer.name)
        }
      }
      return layerUrl ? (
        <V key={`${layer.name}_container`} style={{}}>
          {
            <VItem
              key={layer.translation}
              $space={false}
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <Typography variant="h6" sx={{ display: 'inline' }}>
                {t(layer.translation)}
              </Typography>
              {features?.['edit-legend']['isEnabled'] &&
              view === 'single' &&
              c.customLegendLayerTypes.includes(selectedLayer?.name ?? '') ? (
                <Tooltip
                  title={
                    validSubscription
                      ? t('customize_legend')
                      : t('renew_subscription')
                  }
                  placement="left-start"
                >
                  <span>
                    <IconButton
                      aria-label="edit"
                      onClick={() => customizeLegend(selectedLayer)}
                      size="small"
                      sx={{
                        width: 'auto',
                        justifyContent: 'flex-end',
                        paddingTop: '3px',
                      }}
                      disabled={!validSubscription}
                    >
                      {validSubscription ? (
                        <EditIcon
                          titleAccess={t('customize_legend')}
                          style={{ color: colors.brand }}
                        />
                      ) : (
                        <EditOffIcon style={{ color: colors.dangerLight }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </VItem>
          }
          {getLegend(layer, layerUrl)}
          <LegendTypeSwitch />
        </V>
      ) : null
    })
    _legendItems = rasterItems.concat(vectorItems)
    setLegendItems(_legendItems)
  }, [
    filteredUrls,
    vectorLayers,
    rasterLayers,
    legendTypes,
    selectedLayer,
    selectedSwathParameter,
    layerInfo,
  ])

  const handleOpacityChange = useCallback(
    (event: Event, value: number, activeThumb: number) => {
      dispatch(setLayerOpacity(value))
      invokeDebouncedUsageTracking()
    },
    [],
  )

  const handleLegendTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = event.target.checked
    const legendTypeValue = legendTypes.find(
      (l) =>
        l.harvestId === harvestId &&
        (selectedLayer?.name === 'swath'
          ? l.swathParameter === selectedSwathParameter &&
            l.layerName === 'swath'
          : l.layerName === selectedLayer?.name),
    )
    dispatch(
      setLegendType({
        harvestId: harvestId,
        layerName: legendTypeValue?.layerName ?? selectedLayer?.name,
        swathParameter: selectedSwathParameter,
        useCustomLegend: !legendTypeValue?.useCustomLegend,
      }),
    )

    trackEvent('view-default-legend', {
      customLegend: !legendTypeValue?.useCustomLegend,
      layer: legendTypeValue?.layerName,
      swathParameter: selectedSwathParameter,
    })
  }

  const customizeLegend = (layer) => {
    const title =
      layer.translation === 'swath_map'
        ? !selectedSwathParameter
          ? `${t(layer.translation)} - ${t('yield')}`
          : `${t(layer.translation)} - ${t(selectedSwathParameter)}`
        : t(layer.translation)
    let customLegendData: CustomLegend | undefined = undefined
    let layerDetails: LayerInfoModel | undefined = undefined

    if (layerInfo && layerInfo.length > 0) {
      layerDetails = layerInfo.find(
        (l) => l.harvestId === harvestId && l.layerName === layer.name,
      )

      if (layerDetails) {
        layerDetails.customLegendMetadataParam = getLegendParameterForLayer(
          layer.name,
          selectedSwathParameter,
        )
      }

      if (
        c.customLegendLayerTypes.includes(layer.name) &&
        layerDetails &&
        layerDetails.customLegends &&
        layerDetails.customLegends.length > 0 &&
        layerDetails.customLegends[0] !== null &&
        Array.isArray(layerDetails?.customLegends)
      ) {
        if (layer.name === 'swath') {
          customLegendData = layerDetails.customLegends.find(
            (legend) =>
              legend.parameter === (selectedSwathParameter ?? 'yield'),
          )
          layerDetails.customLegendMetadataParam =
            selectedSwathParameter ?? 'yield'
        } else if (layer.name === 'dem') {
          customLegendData = layerDetails.customLegends.find(
            (legend) => legend.parameter === 'dem',
          )
          layerDetails.customLegendMetadataParam = 'dem'
        } else {
          customLegendData = layerDetails?.customLegends[0]
        }
      }
    }

    dispatch(
      showModal(
        <EditLegend
          harvestId={harvestId}
          layer={pick(layer, ['name', 'translation'])}
          crop={crop}
          customLegend={customLegendData}
          layerDetails={layerDetails}
        />,
        {
          title: title,
          draggable: true,
        },
      ),
    )
  }

  const LegendTypeSwitch = useCallback(() => {
    let showLegendToggle = false
    if (layerInfo && selectedLayer) {
      const layerDetails = layerInfo.find(
        (l) => l.harvestId === harvestId && l.layerName === selectedLayer?.name,
      )

      showLegendToggle =
        Array.isArray(layerDetails?.customLegends) &&
        layerDetails?.customLegends?.length > 0 &&
        !isNil(layerDetails?.customLegends[0]) &&
        layerDetails?.customLegends?.some(
          (legend) =>
            legend.parameter ===
            getLegendParameterForLayer(
              selectedLayer?.name,
              selectedSwathParameter,
            ),
        )
    }
    const legendTypeValue = legendTypes.find(
      (l) =>
        l.harvestId === harvestId &&
        (selectedLayer?.name === 'swath'
          ? l.swathParameter === selectedSwathParameter &&
            l.layerName === 'swath'
          : l.layerName === selectedLayer?.name),
    )

    return (
      <>
        {showLegendToggle ? (
          <FormGroup style={{ display: 'inline-flex' }}>
            <Stack direction="row" sx={{ alignItems: 'center' }}>
              <Typography>{t('default')}</Typography>
              <FormControlLabel
                control={
                  <LegendControlSwitch
                    checked={legendTypeValue?.useCustomLegend ?? true}
                    onChange={handleLegendTypeChange}
                  />
                }
                label=""
              />
              <Typography>{t('custom')}</Typography>
            </Stack>
          </FormGroup>
        ) : null}
      </>
    )
  }, [legendTypes, selectedLayer, layerInfo])

  return filteredUrls.length > 0 ? (
    <FloatingPanel
      $position={'right'}
      key={harvestId}
      $background="white"
      $minWidth="220px"
      id={id}
      data-testid={id}
      draggable={true}
    >
      {legendItems}

      <FormControlLabel
        value="opacity"
        control={
          <Slider
            key={'opacity-slider'}
            aria-label="Opacity"
            value={opacity}
            onChange={handleOpacityChange}
            min={0.0}
            max={1.0}
            step={0.01}
            sx={{
              width: '60%',
              marginRight: '10px',
            }}
            size="small"
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
          />
        }
        label={t('layer_opacity')}
        labelPlacement="start"
        sx={{
          '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem',
          },
        }}
      />

      {isAdmin ? (
        <div style={{ textAlign: 'center' }}>
          <Tooltip title={lpProcessDateTime} placement="top" arrow>
            <span>
              <LegendHeader>{`LP Version: ${lpVersion}`}</LegendHeader>
            </span>
          </Tooltip>
          <Button
            color="secondary"
            variant="contained"
            disabled={histogramLayerName === ''}
            onClick={() => {
              dispatch(
                showModal(
                  <Histogram
                    harvestId={harvestId}
                    layerType={histogramLayerName}
                  />,
                ),
              )
            }}
          >
            {t('view_histogram')}
          </Button>
        </div>
      ) : null}
    </FloatingPanel>
  ) : null
}

const LegendHeader = styled('h1')({
  color: colors.brand,
  fontSize: 'clamp(10px, calc((100vw + 100vh)/2 * 0.01), 15px)',
  maxFontSize: '10px',
  textAlign: 'center',
  margin: 0,
  marginBottom: '8px',
  padding: 0,
})
